import React, { useState, memo, useCallback } from 'react';

import CostCenterItemSelect, { isValidCostCenterItem } from './CostCenterItemSelect';
import ProjectSelect, { isValidProject } from './ProjectSelect';
import OverviewContent, { useControlledTableState } from './OverviewContent';
import { CostCenterItem, OverviewData } from './Types';
import { apiGetOverviewTree, apiSetOverviewAllScopes, apiGetOverview, apiGetCostCenterStatus } from './Api';
import { useApiData, LoadError, Loading } from './useApiData';
import { useScopeKeys, scopedTree } from './Scope';
import AllScopesToggle from './AllScopesToggle';
import BudgetBanner from './BudgetBanner';
import { sumKronor } from './Formatting';


// A voucher can book several rows of one kind to one voucher, so each row's amount is a list.
const sum2 = (amounts: number[]) => sumKronor(amounts)


function getCostCenterItem(costCenterItems: CostCenterItem[], costCenterItemId: string | null) {
    return costCenterItems.find(item => item.cost_center_item_id === costCenterItemId) ?? null
}

const TwoSelector = ({costCenterItems, costCenterItemId, projectId, setCostCenterItemId, setProjectId, disabled}: {
    costCenterItems: CostCenterItem[],
    costCenterItemId : string | null,
    projectId: string | null,
    setCostCenterItemId: (newValue: string) => void,
    setProjectId: (newValue: string | null) => void,
    disabled: boolean
}) => {

    const changeCostCenterItemHandler = useCallback( 
        (newCostCenterItemId: string) => {
            setCostCenterItemId(newCostCenterItemId)
            setProjectId(null)
        },
        [setCostCenterItemId, setProjectId]
    )

    const changeProjectHandler = useCallback( 
        (newProjectId: string) => {
            setProjectId(newProjectId)
        },
        [setProjectId]
    )

    return <>
        <CostCenterItemSelect
            disabled={disabled}
            required={true}
            label='Kostnadsställe'
            costCenterItems={costCenterItems}
            value={costCenterItemId}
            onChange={changeCostCenterItemHandler}
        />
        <ProjectSelect
            disabled={disabled || !costCenterItemId}
            required={true}
            label='Projekt'
            costCenterItem={getCostCenterItem(costCenterItems, costCenterItemId)}
            value={projectId}
            onChange={changeProjectHandler}
        />
        </>
}

const Overview = memo(({refreshSignal}: {refreshSignal: number}) => {
    // Full tree from the server; the selector is filtered to the active role's scope in the browser
    // (exactly like the attest pickers) unless "show all locations" is on — so the toggle is
    // instant, no round-trip. tree.all_scopes seeds this per-user preference and it's persisted
    // best-effort; the figures endpoint (/overview/content) stays the scope boundary server-side.
    const { data: tree, error: treeError, reload: reloadTree } = useApiData(apiGetOverviewTree, [])
    const costCenterItems = tree?.cost_center_items ?? null
    const scopeKeys = useScopeKeys()
    const [allScopesOverride, setAllScopesOverride] = useState<boolean | null>(null)
    const allScopes = allScopesOverride ?? (tree?.all_scopes ?? false)
    const toggleAllScopes = useCallback(() => {
        const next = !allScopes
        setAllScopesOverride(next)                                             // flips instantly
        apiSetOverviewAllScopes(next).catch(() => setAllScopesOverride(!next))  // best-effort; revert on failure
    }, [allScopes])
    const [costCenterItemId, setCostCenterItemId] = useState<string | null>(null)
    const [projectId, setProjectId] = useState<string | null>(null)

    // The kostnadsställe's budget and result, fetched as soon as one is chosen. It needs no project
    // — a budget is set per kostnadsställe — and it used to ride along with the rows, so a reader
    // had to pick a project before their own section's figures would appear. It is also the cheaper
    // request, so the band arrives while the tables are still loading.
    const { data: costCenterStatus } = useApiData(
        () => apiGetCostCenterStatus(costCenterItemId as string),
        [costCenterItemId, refreshSignal],
        { enabled: costCenterItemId !== null })

    // Sorting/filtering live here, above the content, so they outlive the reloads and
    // selector changes that unmount OverviewContent (e.g. picking a cost-center clears
    // the project, hiding the tables until a new one is chosen).
    const incomeTableState = useControlledTableState()
    const expenseTableState = useControlledTableState()

    // The content reloads automatically whenever the selected project changes, and
    // whenever `refreshSignal` is bumped (another tab changed data the overview shows).
    // Passing the signal as a fetch dependency refetches in place — the table instance,
    // sorting and filtering are preserved — without remounting or a separate effect.
    const contentEnabled = costCenterItemId !== null && projectId !== null
    const {
        data,
        loading: contentLoading,
        error: contentError,
        setData,
        reload: reloadContent,
    } = useApiData(
        () => apiGetOverview({ cost_center_item_id: costCenterItemId as string, project_id: projectId as string }),
        [costCenterItemId, projectId, refreshSignal],
        { enabled: contentEnabled },
    )

    const setNonNullData = useCallback(
        (modifier: (nonNullData: OverviewData) => void) => {
            setData((data: OverviewData | null) => { if (data !== null) modifier(data) })
        },
        [setData]
    )

    if (treeError !== null) {
        return <LoadError onRetry={reloadTree}/>
    }
    if (tree === null || costCenterItems === null) {
        return <Loading/>
    }

    const valid = isValidCostCenterItem({costCenterItems, costCenterItemId}) &&
        isValidProject({projects: getCostCenterItem(costCenterItems, costCenterItemId)?.projects ?? [], project_id: projectId})

    // Summed from the rows already on screen rather than computed again on the server, so the figure
    // is one a reader can check: it is exactly the tables below it, added up. A second server-side
    // computation could drift from what is displayed; this cannot. Income and expense rows carry no
    // balance-sheet accounts, so their difference is a result.
    const projectName = getCostCenterItem(costCenterItems, costCenterItemId)
        ?.projects.find(project => project.project_id === projectId)?.ProjectName
    const projectResult = (valid && data !== null && projectName !== undefined)
        ? {
            name: projectName,
            result: sumKronor(data.incomes.map(income => sum2(income.IncomeAmount)))
                - sumKronor(data.expenses.map(expense => sum2(expense.ExpenseAmount))),
        }
        : null

    // Offer only the active role's pairs (or everything when "show all" is on — null keys ⇒ no
    // filter). validity/name resolution above uses the full tree, so a selected pair never vanishes.
    const scopedItems = scopedTree(costCenterItems, allScopes ? null : scopeKeys, costCenterItemId, projectId)

    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-start', gap: '1rem' }}>
            <div style={{ display: 'flex', flexFlow: 'row nowrap', gap: '1.5rem' }}>
                <TwoSelector
                    costCenterItems={scopedItems}
                    costCenterItemId={costCenterItemId}
                    projectId={projectId}
                    setCostCenterItemId={setCostCenterItemId}
                    setProjectId={setProjectId}
                    disabled={valid && contentLoading}
                />
            </div>
            <AllScopesToggle checked={allScopes} onChange={toggleAllScopes}/>
            {costCenterStatus?.budget != null &&
                <BudgetBanner
                    status={costCenterStatus.budget}
                    costCenterItemName={getCostCenterItem(costCenterItems, costCenterItemId)?.CostCenterItemName ?? ''}
                    project={projectResult}
                />
            }
            {/* Keep the tables mounted whenever we have data, even while a reload is in
                flight, so a refresh updates them in place (no teardown, no flash) and
                their sorting/filtering — held above in Overview — is preserved. The bare
                spinner/error only show when there is nothing to display yet. */}
            {valid && data !== null &&
                <OverviewContent
                    data={data}
                    setData={setNonNullData}
                    loading={contentLoading}
                    incomeTableState={incomeTableState}
                    expenseTableState={expenseTableState}
                />
            }
            {valid && data === null && contentLoading &&
                <Loading/>
            }
            {valid && data === null && !contentLoading && contentError !== null &&
                <LoadError onRetry={reloadContent}/>
            }
        </div>
    )
})


export default Overview
